import { useEffect, useId, useRef, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { Link } from "react-router";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { useAccountQuery } from "../utils/queries";
import { communityJoinPath, ownerPlan } from "../utils/communityEntitlements.mjs";

function ExploreUpgrade({ plan }) {
  const [open, setOpen] = useState(false);
  const container = useRef(null);
  const trigger = useRef(null);
  const tooltipId = useId();
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!container.current?.contains(event.target)) setOpen(false); };
    const escape = event => { if (event.key === "Escape") { setOpen(false); trigger.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return <span className="mr-3 inline-flex shrink-0 items-center gap-0.5 text-sm text-[var(--app-text)] sm:mr-5">
    <Link to="/choose-plan" className="inline-flex min-h-9 items-center font-semibold underline">Upgrade</Link>
    <span ref={container} className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <button ref={trigger} type="button" aria-label="Why upgrade?" aria-expanded={open} aria-controls={tooltipId} aria-describedby={open ? tooltipId : undefined} onFocus={() => setOpen(true)} onClick={() => setOpen(true)} className="inline-flex min-h-9 w-4 items-center justify-center bg-transparent"><FiInfo aria-hidden="true" className="h-3.5 w-3.5" /></button>
      {open && <span className="absolute right-0 top-full z-50 w-56 max-w-[calc(100vw-2rem)] pt-2"><span id={tooltipId} role="tooltip" style={{ backgroundColor: "var(--app-panel, #ffffff)" }} className="block rounded-md p-3 text-sm font-normal text-[var(--app-text)] shadow-lg ring-1 ring-[var(--app-border)]">{plan === "plus" ? "Upgrade for more features" : "Upgrade to host videos and sell content"}</span></span>}
    </span>
  </span>;
}

export default function HeaderUpgrade({ community }) {
  const { userId, userInfo, authLoading } = useAuth();
  const { socket } = useSocket();
  const owner = community && String(community.creatorId?._id || community.creatorId) === String(userId);
  const customer = Boolean(userId && community && !owner);
  const memberships = useAccountQuery(["header-memberships"], "/group-purchases/memberships", {
    enabled: customer,
    staleTime: 0,
    refetchOnMount: "always",
  });
  useEffect(() => {
    if (!customer) return;
    const refresh = () => memberships.refetch();
    socket?.on("subscription:updated", refresh);
    socket?.on("purchase:updated", refresh);
    socket?.on("connect", refresh);
    return () => {
      socket?.off("subscription:updated", refresh);
      socket?.off("purchase:updated", refresh);
      socket?.off("connect", refresh);
    };
  }, [socket, customer, memberships.refetch]);
  if (!userId || authLoading) return null;
  if (community && owner) return null;
  let to, label;
  if (!community || owner) {
    if (!userInfo?.subscriptionInfo || ownerPlan(userInfo.subscriptionInfo) === "pro") return null;
    to = "/choose-plan";
    label = "Upgrade plan";
  } else {
    const membership = memberships.data?.memberships?.find(item => String(item.group?._id) === String(community._id));
    if (memberships.isFetching || memberships.isError || !membership || membership.paid || community.pricing?.access !== "paid" || !community.store?.enabled) return null;
    to = communityJoinPath(community.slug, true);
    label = "Upgrade to premium";
  }
  if (!community) return <ExploreUpgrade plan={ownerPlan(userInfo.subscriptionInfo)} />;
  return <Link to={to} className="inline-flex min-h-9 shrink-0 items-center justify-center rounded-md bg-orange-700 px-2 text-xs font-semibold text-white hover:bg-orange-800 sm:px-3 sm:text-sm">{label}</Link>;
}
